import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';

const Home: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && user) {
      navigate('/dashboard');
    }
  }, [user, isLoading, navigate]);

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-vh-100 d-flex justify-content-center align-items-center bg-gradient-primary">
        <div className="spinner-border text-white" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Don't render home page if user is authenticated
  if (user) {
    return null;
  }

  return (
    <div className="min-vh-100 bg-gradient-primary">
      {/* Navigation */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-dark">
        <div className="container">
          <Link className="navbar-brand d-flex align-items-center" to="/">
            <Logo size="sm" className="me-2" />
            <span className="fw-bold">Dream Digi</span>
          </Link>
          
          <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
            <span className="navbar-toggler-icon"></span>
          </button>
          
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ms-auto">
              <li className="nav-item">
                <Link className="nav-link" to="/login">Login</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link btn btn-primary ms-2" to="/register">Get Started</Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="container-fluid py-5">
        <div className="container">
          <div className="row align-items-center min-vh-75">
            <div className="col-lg-6">
              <div className="text-center text-lg-start">
                <div className="d-flex justify-content-center justify-content-lg-start mb-4">
                  <Logo size="lg" />
                </div>
                <h1 className="display-4 fw-bold text-white mb-4">
                  Dream Digi Page Optimization with AI
                </h1>
                <p className="lead text-white-50 mb-5">
                  Transform your website with AI-powered SEO, GEO, and performance optimization. 
                  Get real-time analytics, competitor insights, and automated recommendations.
                </p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-start">
                  <Link to="/register" className="btn btn-primary btn-lg px-4 py-3">
                    Start Free Trial
                  </Link>
                  <Link to="/login" className="btn btn-outline-light btn-lg px-4 py-3">
                    Sign In
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="text-center">
                <div className="card bg-white bg-opacity-10 border-0 rounded-4 p-4">
                  <div className="card-body">
                    <h5 className="text-white mb-3">Live Demo Dashboard</h5>
                    <div className="bg-dark rounded-3 p-3">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center">
                          <div className="bg-primary rounded-circle me-2" style={{width: '12px', height: '12px'}}></div>
                          <span className="text-white-50 small">Dream Digi Analytics</span>
                        </div>
                        <div className="text-white-50 small">75% Visibility</div>
                      </div>
                      <div className="progress mb-3" style={{height: '8px'}}>
                        <div className="progress-bar bg-primary" style={{width: '75%'}}></div>
                      </div>
                      <div className="row text-center">
                        <div className="col-4">
                          <div className="text-white fw-bold">227</div>
                          <div className="text-white-50 small">Mentions</div>
                        </div>
                        <div className="col-4">
                          <div className="text-white fw-bold">1,912</div>
                          <div className="text-white-50 small">Citations</div>
                        </div>
                        <div className="col-4">
                          <div className="text-white fw-bold">#1</div>
                          <div className="text-white-50 small">Position</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="container py-5">
        <div className="row g-4">
          <div className="col-md-4">
            <div className="card h-100 border-0 bg-white bg-opacity-10 text-white">
              <div className="card-body text-center p-4">
                <div className="bg-primary rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '60px', height: '60px'}}>
                  <i className="fas fa-chart-line fa-2x"></i>
                </div>
                <h5 className="card-title">AI-Powered Analytics</h5>
                <p className="card-text text-white-50">
                  Get deep insights into your website performance with advanced AI analytics and real-time monitoring.
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card h-100 border-0 bg-white bg-opacity-10 text-white">
              <div className="card-body text-center p-4">
                <div className="bg-success rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '60px', height: '60px'}}>
                  <i className="fas fa-users fa-2x"></i>
                </div>
                <h5 className="card-title">Competitor Analysis</h5>
                <p className="card-text text-white-50">
                  Track your competitors and understand market positioning with comprehensive analysis tools.
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card h-100 border-0 bg-white bg-opacity-10 text-white">
              <div className="card-body text-center p-4">
                <div className="bg-warning rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '60px', height: '60px'}}>
                  <i className="fas fa-magic fa-2x"></i>
                </div>
                <h5 className="card-title">Smart Optimization</h5>
                <p className="card-text text-white-50">
                  Automatically optimize your content with AI recommendations for SEO, GEO, and performance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-dark py-4 mt-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-4">
              <div className="d-flex align-items-center">
                <Logo size="sm" className="me-2" />
                <span className="text-white fw-bold">Dream Digi</span>
              </div>
            </div>
            <div className="col-md-4 text-center">
              <div className="d-flex justify-content-center gap-3">
                <Link to="/terms" className="text-white-50 text-decoration-none small">
                  Terms of Service
                </Link>
                <Link to="/privacy" className="text-white-50 text-decoration-none small">
                  Privacy Policy
                </Link>
              </div>
            </div>
            <div className="col-md-4 text-md-end">
              <p className="text-white-50 mb-0">&copy; 2025 Dream Digi. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
